import dayjs from 'dayjs';
import type {
  Chapter,
  CityPR,
  TestResult,
  TestResultStatistics,
  TestResultStatisticsSection,
} from '@/types';

export const cityGoals: CityPR[] = [
  { name: 'Wien', pr: 82 },
  { name: 'Innsbruck', pr: 68 },
  { name: 'Graz', pr: 58 },
  { name: 'Salzburg', pr: 57 },
];

export type FeedbackTone = 'firstRun' | 'improved' | 'steady' | 'declined';

export function getOverallPercentage(statistics: TestResultStatistics): number {
  if (statistics.questions === 0) return 0;
  return Math.ceil((statistics.correct / statistics.questions) * 100);
}

export function getPoints(statistics: TestResultStatistics): number {
  return statistics.correct * 1.25;
}

export function getMaxPoints(statistics: TestResultStatistics): number {
  return statistics.questions * 1.25;
}

export type CityGoalStatus = {
  name: string;
  pr: number;
  met: boolean;
};

export function getCityGoalStatus(percentageRanking?: number): CityGoalStatus[] {
  const userPr = percentageRanking != null && percentageRanking > 0 ? percentageRanking : 0;
  return cityGoals.map((city) => ({
    name: city.name,
    pr: city.pr,
    met: userPr >= city.pr,
  }));
}

export function getSectionPercentage(section: TestResultStatisticsSection): number {
  return getOverallPercentage(section.statistics);
}

export function getImprovementDelta(latest: TestResult, previous?: TestResult): number | undefined {
  if (!previous) return undefined;
  return getOverallPercentage(latest.statistics) - getOverallPercentage(previous.statistics);
}

export function getFeedbackTone(delta: number | undefined): FeedbackTone {
  if (delta === undefined) return 'firstRun';
  if (delta > 0) return 'improved';
  if (delta < 0) return 'declined';
  return 'steady';
}

export function getWeakestSection(
  sections: TestResultStatisticsSection[],
): TestResultStatisticsSection | undefined {
  if (!sections.length) return undefined;

  return sections.reduce((weakest, section) => {
    const weakestRatio = weakest.statistics.correct / (weakest.statistics.questions || 1);
    const sectionRatio = section.statistics.correct / (section.statistics.questions || 1);
    return sectionRatio < weakestRatio ? section : weakest;
  });
}

export function getStrongestSection(
  sections: TestResultStatisticsSection[],
): TestResultStatisticsSection | undefined {
  if (!sections.length) return undefined;

  return sections.reduce((strongest, section) => {
    const strongestRatio = strongest.statistics.correct / (strongest.statistics.questions || 1);
    const sectionRatio = section.statistics.correct / (section.statistics.questions || 1);
    return sectionRatio > strongestRatio ? section : strongest;
  });
}

export function getPerformanceColor(percentage: number): string {
  if (percentage >= 90) return '#28a745';
  if (percentage >= 75) return '#7ec05d';
  if (percentage >= 50) return '#ffc107';
  return '#dc3545';
}

export function getNearestCityGoal(percentageRanking?: number): CityPR | undefined {
  if (percentageRanking == null || percentageRanking <= 0) {
    return cityGoals[0];
  }

  const unmetGoals = cityGoals.filter((city) => percentageRanking < city.pr);
  return unmetGoals[0] ?? cityGoals[cityGoals.length - 1];
}

export function resolveExerciseSlug(
  curriculum: Chapter[],
  exerciseIds?: string[],
): string | undefined {
  if (!exerciseIds?.length) return undefined;

  const exerciseId = exerciseIds[0];
  const match = curriculum.find((item) => item.type === 'Exercise' && item.item.id === exerciseId);

  return match?.item.slug;
}

export function getTrendData(testResults: TestResult[]) {
  return [...testResults].reverse().map((result, index) => {
    const run = result.testRun ?? index + 1;
    return {
      run,
      percentage: getOverallPercentage(result.statistics),
      createdAt: result.createdAt,
    };
  });
}

export function testAtMatches(a?: string, b?: string): boolean {
  if (!a || !b) return false;
  return dayjs(a).isSame(dayjs(b), 'day');
}

export function getCohortResults(allResults: TestResult[], cohortTestAt?: string): TestResult[] {
  if (!cohortTestAt) return [];
  return allResults.filter((result) => testAtMatches(result.testAt, cohortTestAt));
}

export function getFocusSections(
  sections: TestResultStatisticsSection[],
): TestResultStatisticsSection[] {
  if (!sections.length) return [];

  const sortedSections = [...sections].sort(
    (a, b) => getSectionPercentage(a) - getSectionPercentage(b),
  );
  const belowThreshold = sortedSections.filter((section) => getSectionPercentage(section) < 60);

  if (belowThreshold.length > 0) {
    return belowThreshold;
  }

  return [sortedSections[0]];
}
